import { create } from 'zustand';

// Global dwell-timer for the lesson "Mark complete" gate.
//
// Rules:
//   - Only ONE lesson can have a running timer at any moment. Expanding a
//     different lesson preempts the previous one (its accumulated dwell is
//     discarded — start over for the new lesson).
//   - The timer only runs while the lesson's expanded body is open. Collapsing
//     pauses; re-expanding the same lesson resumes from where it stopped.
//   - Once accumulated dwell crosses DWELL_THRESHOLD_SEC (20s), the lesson is
//     "ready" and the Mark complete button can fire.
//
// The timer is held in a single store so two cards can't accumulate dwell in
// parallel — that's how a barber would farm completions otherwise.

export const DWELL_THRESHOLD_SEC = 20;

type State = {
  activeLessonId: string | null;
  secs: number;
  // Internal — RAF/interval handle so we can stop cleanly. Not part of the
  // selector surface; consumers don't read it.
  _intervalId: number | null;
};

type Actions = {
  /** Called when a lesson's expanded body mounts or becomes visible. */
  open(lessonId: string): void;
  /** Called when a lesson's expanded body unmounts (collapse). */
  close(lessonId: string): void;
  /** Reset dwell on a specific lesson — useful after marking complete so the
   *  timer doesn't carry over if the user re-expands the same lesson. */
  resetIfActive(lessonId: string): void;
};

export const useLessonDwell = create<State & Actions>((set, get) => ({
  activeLessonId: null,
  secs: 0,
  _intervalId: null,

  open(lessonId) {
    const cur = get();
    // Switching to a new lesson — reset the counter.
    if (cur.activeLessonId !== lessonId) {
      if (cur._intervalId != null) clearInterval(cur._intervalId);
      const id = window.setInterval(() => {
        set((s) => ({ secs: s.secs + 1 }));
      }, 1000);
      set({ activeLessonId: lessonId, secs: 0, _intervalId: id });
      return;
    }
    // Resuming the same lesson — keep secs, restart interval if it was paused.
    if (cur._intervalId == null) {
      const id = window.setInterval(() => {
        set((s) => ({ secs: s.secs + 1 }));
      }, 1000);
      set({ _intervalId: id });
    }
  },

  close(lessonId) {
    const cur = get();
    if (cur.activeLessonId !== lessonId) return;
    if (cur._intervalId != null) {
      clearInterval(cur._intervalId);
      set({ _intervalId: null });
    }
  },

  resetIfActive(lessonId) {
    const cur = get();
    if (cur.activeLessonId !== lessonId) return;
    if (cur._intervalId != null) clearInterval(cur._intervalId);
    set({ activeLessonId: null, secs: 0, _intervalId: null });
  },
}));

/** Selector hook: returns { secs, ready } for a given lesson. Re-renders only
 *  when the active lesson is `lessonId` (so non-active cards don't tick). */
export function useDwellForLesson(lessonId: string): { secs: number; ready: boolean } {
  return useLessonDwell((s) => {
    const isActive = s.activeLessonId === lessonId;
    const secs = isActive ? s.secs : 0;
    return { secs, ready: secs >= DWELL_THRESHOLD_SEC };
  });
}
